import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { toJS } from 'mobx';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import { resolveEventColor } from '../../lib/eventColor';
import { getCalendarColorIdx, getCalendarBackgroundColor } from '../../lib/calendarColor';

const DEFAULT_HEIGHT = 80; // 60 minutes per hour * 1.2px per minute
const DEFAULT_HEIGHT_SCALE = DEFAULT_HEIGHT / 60;

const formatTime = iso => iso ? new Date(iso).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }) : '-';
const clampPos = (x, y, w = 320, h = 180, m = 12) => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
    let left = x + m;
    let top = y + m;
    if (left + w > vw) left = Math.max(m, vw - w - m);
    if (top + h > vh) top = Math.max(m, vh - h - m);
    return { left, top };
};

// Optional: trim long text
const ellipsize = (str, max = 140) => {
    if (!str) return '';
    return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};

// --- small preview component ---
const EventPreview = ({ ev, currentVisitor, pos, color, bgColor }) => {
    const width = 320;
    const height = 'auto';
    return (
        <div
            class='position-fixed shadow rounded-lg'
            style={`
                left: ${pos.left}px;
                top: ${pos.top}px;
                width: ${width}px;
                max-width: 90vw;
                pointer-events: none; /* tooltip behavior */
                z-index: 10001;
                background: ${bgColor || '#fff'};
                color: ${color || '#222'};
                border: 1px solid rgba(0,0,0,0.1);
                padding: 10px 12px;
                line-height: 1.2;
            `}
            role='tooltip'
        >
            <div class='d-flex align-items-center mb-1'>
                <div class='me-2' style={`width: 8px; height: 24px; background: ${color}; border-radius: 2px;`} />
                <div class='font-weight-bold' style='font-size: 0.95rem;'>
                    {ev.skipReminders && <i class='fa-duotone fa-bell-slash text-danger me-1' />}
                    {ev.isRecurring && <i class='fa-duotone fa-repeat me-1' />}
                    {ev.title || 'Uten tittel'}
                </div>
            </div>
            <div class='text-muted' style='font-size: 0.85rem;'>
                {formatTime(ev.start)}–{formatTime(ev.end)}
                {ev.location && <> · {ev.location}</>}
            </div>
            {ev.body && <div class='mt-2' style='font-size: 0.9rem;'>{ellipsize(ev.body, 180)}</div>}

            {currentVisitor && currentVisitor.firstname && <>
                <div class='text-muted mt-2 pt-2 border-top' style='font-size: 0.85rem;'>
                    {currentVisitor.firstname} {currentVisitor.lastname}<br />
                    {currentVisitor.address}, {currentVisitor.postalcode} {currentVisitor.place}<br />
                    {currentVisitor.cellphone}
                </div>
            </>}
        </div>
    );
};

@observer
class CalendarHourGrid extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            isDragging: false,
            hoverEvent: null,
            previewPos: { left: 0, top: 0 },
        };
        this.hoverShowTimer = null;
        this.hoverHideTimer = null;
    }

    getHeightConfig = () => {
        const { userStore } = this.props.stores;
        const { customer = {} } = userStore;
        const { featureFlags = {} } = customer;
        const HEIGHT = parseInt(featureFlags.calendarHourHeight, 10) || DEFAULT_HEIGHT;
        const HEIGHT_SCALE = HEIGHT / 60;
        return { HEIGHT, HEIGHT_SCALE };
    }

    loadAll = () => {
        const { calendarStore, calendarEventStore } = this.props.stores;
        calendarStore.setStartHour(0);
        calendarStore.setEndHour(24);
        calendarStore.getHoursInDay();
    }

    viewEvent = e => {
        e.stopPropagation();
        e.preventDefault();

        const { showViewEvent = true } = this.props;
        if (!showViewEvent) {
            return;
        }

        const eventEl = e.target.closest('.calendar-event');
        if (!eventEl) {
            console.warn('No calendar-event element found');
            return;
        }

        // Conventions are synthetic events (id 'convention-<id>'); open the
        // convention page instead of the booking drawer.
        const { conventionId } = eventEl.dataset;
        if (conventionId) {
            route(`/convention/${conventionId}`);
            return;
        }

        const { id, start: eventStart } = eventEl.dataset;

        // Validate event ID
        if (!id || id === 'undefined' || id === 'null') {
            console.warn('No valid event ID found on clicked event');
            return;
        }

        const parsedId = parseInt(id, 10);
        if (isNaN(parsedId)) {
            console.warn('Invalid event ID:', id);
            return;
        }

        const { appState, calendarStore, calendarEventStore } = this.props.stores;
        const { eventId } = calendarStore
        if (eventId) {
            return;
        }

        const { drawerLevel = 1, callback = () => {} } = this.props;
        const { drawerHeightMediumLarge } = appState;

        appState.openDrawer('viewCalendarEvent', {
            height: drawerHeightMediumLarge,
            id: parsedId,
            eventStart: eventStart || null,
            showEdit: true,
            callback: event => {
                if (event?.excludeDate) {
                    const dateStr = new Date(eventStart).toISOString().slice(0, 10);
                    const filtered = calendarEventStore.calendarEvents.filter(e => {
                        if (e.id !== event.id) return true;
                        const eDate = new Date(e.start).toISOString().slice(0, 10);
                        return eDate !== dateStr;
                    });
                    calendarEventStore.updateKeyValue('calendarEvents', filtered);
                }
                callback(event);
                appState.toggleDrawer(false, drawerLevel + 2);
                calendarStore.resetDragging();
            },
        }, drawerLevel + 2);
    }

    selectTime = e => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        const { isDragging } = this.state;
        if (isDragging) {
            return;
        }
        const { enableSelection } = this.props;
        if (!enableSelection) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        const { hour, date } = e.target.closest('.hour-line').dataset;
    // console.log('selectTime', { hour, date })
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;  // y position within the element


        // Round to the nearest 15 minutes
        const roundedMinutes = Math.round((y / HEIGHT_SCALE) / 15) * 15;
        // Zero pad minutes if needed
        let paddedMinute = roundedMinutes < 10 ? `0${roundedMinutes}` : roundedMinutes;
        let hourInt = parseInt(hour, 10);
        // console.log('y', y, 'roundedMinutes', roundedMinutes, 'hourInt', hourInt)
        if (roundedMinutes === 60) {
            hourInt += 1;
            // console.log('y', y, 'yRelative', yRelative, 'minScale', minScale, 'roundedMinutes', roundedMinutes, 'hourInt', hourInt)
            paddedMinute = '00';
        }
        const paddedHour = hourInt < 10 ? `0${hourInt}` : hourInt;

        const { appState, saleStore, calendarStore } = this.props.stores;
        // const { calendarDate } = calendarStore;
        const { calendarDate } = this.props;
        // calendarStore.resetDragging();

        const startDate = new Date(`${util.isoDate(calendarDate, false, false, true)}T${paddedHour}:${paddedMinute}:00`);
        const endDate = new Date(startDate);
        endDate.setMinutes(endDate.getMinutes() + 15);
        // console.log(`paddedHour: ${paddedHour}, paddedMinute: ${paddedMinute}`)
        // console.log(`startDate: ${startDate}, endDate: ${endDate}`)
        const { calendarSelectDateTime, calendarId, callback = () => {} } = this.props;

        if (calendarSelectDateTime) {
            calendarSelectDateTime({
                start: startDate,
                end: endDate,
                calendarId,
            });
            return;
        }

        const { drawerLevel = 1, skipAnimal } = this.props;
        const { drawerHeightLarge } = appState;
        const { newSale } = saleStore;
        const { animals } = newSale;
        appState.openDrawer('newCalendarEvent', {
			height: drawerHeightLarge,
            isNew: true,
            hideAdmin: true,
            calendarId,
            calendarType: 'booking',
            animals,
            startDate: startDate,
            endDate: endDate,
            skipAnimal,
            callback: async booking => {
                // console.log('newCalendarEvent callback', booking);
                appState.toggleDrawer(false, drawerLevel + 1);
                callback(booking);
                calendarStore.resetDragging();
            },
		}, drawerLevel + 1);

    }

    handleMouseDown = e => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        // console.log('handleMouseDown');
        const  { calendarId, showPullDownHandle, showViewEvent = true, enableSelection = true } = this.props;
        const { calendarStore } = this.props.stores;
        const { selectionStartDate, selectionEndDate, selectionCalendarId } = calendarStore;
        const selectionDiv = e.target.closest('.selection-calendar-event')
        const pullDownHandle = e.target.closest('.pull-down-handle');
        const eventDiv = e.target.closest('.calendar-event');

// console.log('handleMouseDown', { showViewEvent, enableSelection, selectionStartDate, selectionEndDate, selectionCalendarId, selectionDiv, pullDownHandle, eventDiv })

        if (showPullDownHandle && pullDownHandle) {
            return this.handleMouseDownPullDownHandle(e);
        }
        if (selectionDiv) {
            return this.clickSelection(e);
        }
        if (eventDiv) {
            if (showViewEvent) {
                return this.viewEvent(e);
            }
            return this.handleMouseDownInsideEvent(e);
        }

        if (!enableSelection) {
            return;
        }
// console.log('handleMouseDown: isDragging');
        this.setState({
            isDragging: false,
        });

        const { hour, date } = e.target.closest('.hour-line').dataset;
// console.log('handleMouseDown', { hour, date })
        const rect = e.target.closest('.hour-line').getBoundingClientRect();
        const y = e.clientY - rect.top;  // y position within the element
        // Round to the nearest 15 minutes
        let roundedMinutes = Math.round((y / HEIGHT_SCALE) / 15) * 15;
        // Zero pad minutes if needed
        let paddedMinute = roundedMinutes < 10 ? `0${roundedMinutes}` : roundedMinutes;
        let hourInt = parseInt(hour, 10);
        if (paddedMinute === 60) {
            hourInt += 1;
            paddedMinute = '00';
            roundedMinutes = 0;
        }

        // const { calendarDate } = calendarStore;
        const { calendarDate } = this.props;

        const paddedHour = hourInt < 10 ? `0${hourInt}` : hourInt;

        // console.log('handleMouseDown', { hour, paddedHour, paddedMinute })
        const startDate = new Date(`${util.isoDate(calendarDate, false, false, true)}T${paddedHour}:${paddedMinute}:00`);
        const endDate = new Date(startDate);
        endDate.setMinutes(endDate.getMinutes() + 15);

// console.log('handleMouseDown', { startDate, endDate, calendarId })

        calendarStore.setSelectionStartDate(startDate);
        calendarStore.setSelectionEndDate(endDate);
        calendarStore.setSelectionCalendarId(calendarId);
        calendarStore.setDragging(true);
        calendarStore.setStartY(roundedMinutes * HEIGHT_SCALE);
        calendarStore.setStartYAbs(e.clientY * HEIGHT_SCALE);
        calendarStore.setEndY(e.clientY * HEIGHT_SCALE);
    }

    handleMouseDownInsideEvent = e => {
        e.stopPropagation();
        e.preventDefault();
        // console.log('handleMouseDownInsideEvent', e);
    }

    handleMouseMove = (e) => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        this.setState({
            isDragging: true,
        });
        const { calendarStore, calendarEventStore } = this.props.stores;
        const {
            calendarDate,
            colors,
            calendarHours,
            startY,
            startYAbs,
            endY,
            selectionStartDate,
            selectionEndDate,
            dragging,
            eventId,
            eventEnd,
        } = calendarStore;

        if (dragging) {
            calendarStore.setEndY(e.clientY * HEIGHT_SCALE);
            const selectHeight = endY - startYAbs < 0 ? -Math.abs(endY - startYAbs) : Math.abs(endY - startYAbs);

            // let roundedMinutes = Math.round((y / HEIGHT_SCALE) / 15) * 15;
            // console.log('handleMouseMove', { dragging, startY, startYAbs, endY, eventId, eventEnd, selectHeight })

            const roundedSelectHeight = Math.round((selectHeight / HEIGHT_SCALE) / 15) * 15;

            const endDate = new Date(selectionStartDate);
            endDate.setMinutes(endDate.getMinutes() + roundedSelectHeight);
            calendarStore.setSelectionEndDate(endDate);

            if (eventId) {
                // const rect = e.target.closest('.hour-line').getBoundingClientRect();
                // const y = e.clientY - rect.top;  // y position within the element
                // Round to the nearest 15 minutes
                // let roundedMinutes = Math.round((y / HEIGHT_SCALE) / 15) * 15;

                const roundedMinutesDiff = Math.round((selectHeight / HEIGHT_SCALE));

                // console.log('handleMouseMove', { dragging, startY, startYAbs, endY, eventId, eventEnd, selectHeight, roundedSelectHeight, roundedMinutesDiff })

                const event = calendarEventStore.calendarEvents.find(e => e.id === eventId);
                const end = new Date(eventEnd);
                end.setMinutes(end.getMinutes() + roundedMinutesDiff);
                // end.setMinutes(roundedMinutes);
                calendarEventStore.updateField(eventId, 'end', end);
            }
        }
    }

    handleMouseUp = e => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        const { isDragging } = this.state;
        if (isDragging) {
            setTimeout(() => {
                this.setState({
                    isDragging: false,
                });
            }, 350);
        }
        const { calendarStore, calendarEventStore } = this.props.stores;
        calendarStore.setDragging(false);

        const {
            selectionStartDate,
            selectionEndDate,
            selectionCalendarId,
            startYAbs,
            endY,
            eventId,
            eventEnd,
        } = calendarStore;
        const { calendarSelectDateTime, calendarId, enableSelection } = this.props;
        // console.log('handleMouseUp', { enableSelection, calendarSelectDateTime, selectionStartDate, selectionEndDate, selectionCalendarId, startYAbs, endY, eventId, eventEnd })
        if (enableSelection && calendarSelectDateTime) {
            calendarSelectDateTime({
                start: selectionStartDate,
                end: selectionEndDate,
                calendarId: selectionCalendarId,
            });
            // calendarStore.resetDragging();
            // calendarStore.setSelectionStartDate(null)
            // calendarStore.setSelectionEndDate(null)
            return;
        }
        if (eventId) {
            e.preventDefault();
            e.stopPropagation();
            const selectHeight = endY - startYAbs < 0 ? -Math.abs(endY - startYAbs) : Math.abs(endY - startYAbs);
            const roundedSelectHeight = Math.round((selectHeight / HEIGHT_SCALE) / 15) * 15;

            const event = calendarEventStore.calendarEvents.find(e => e.id === eventId);
            const end = new Date(eventEnd);
            end.setMinutes(end.getMinutes() + roundedSelectHeight);
            calendarEventStore.save({
                end,
            }, eventId);
            calendarEventStore.updateField(eventId, 'end', end);
            setTimeout(() => {
                calendarStore.setEventId(null);
                calendarStore.setEndOfEvent(null);
            }, 500);
        }
    }

    handleMouseDownPullDownHandle = e => {
        const { HEIGHT, HEIGHT_SCALE } = this.getHeightConfig();
        e.stopPropagation();
        e.preventDefault();
        const { calendarStore, calendarEventStore } = this.props.stores;
        // console.log('handleMouseDownPullDownHandle');
        calendarStore.setDragging(true);
        // calendarStore.setStartYAbs(e.clientY);
        // calendarStore.setEndY(e.clientY);

        calendarStore.setStartYAbs(e.clientY * HEIGHT_SCALE);
        calendarStore.setEndY(e.clientY * HEIGHT_SCALE);

        const pullDownHandle = e.target.closest('.pull-down-handle');
        const { id } = pullDownHandle.dataset;
        const eventId = parseInt(id, 10);
        calendarStore.setEventId(eventId);
        const event = calendarEventStore.calendarEvents.find(e => e.id === eventId);
        calendarStore.setEndOfEvent(event.end);

        return;
        calendarStore.setEndY(e.clientY + 15 * HEIGHT_SCALE);

        // Round to the nearest 15 minutes
        // const roundedMinutes = Math.round((y / HEIGHT_SCALE) / 15) * 15;
        // // Zero pad minutes if needed
        // let paddedMinute = roundedMinutes < 10 ? `0${roundedMinutes}` : roundedMinutes;
        // let hourInt = parseInt(hour, 10);
        // // console.log('y', y, 'roundedMinutes', roundedMinutes, 'hourInt', hourInt)
        // if (roundedMinutes === 60) {
        //     hourInt += 1;
        //     // console.log('y', y, 'yRelative', yRelative, 'minScale', minScale, 'roundedMinutes', roundedMinutes, 'hourInt', hourInt)
        //     paddedMinute = '00';
        // }
        // const paddedHour = hourInt < 10 ? `0${hourInt}` : hourInt;

        // calendarStore.setStartY(roundedMinutes * HEIGHT_SCALE);
        // calendarStore.setStartYAbs(e.clientY * HEIGHT_SCALE);
        // calendarStore.setEndY(e.clientY * HEIGHT_SCALE);

    }

    clickSelection = e => {
        e.stopPropagation();
        e.preventDefault();
        // console.log('clickSelection');
        const { calendarStore, appState, saleStore } = this.props.stores;
        const { selectionStartDate, selectionEndDate, selectionCalendarId } = calendarStore;
        const { calendarSelectDateTime, calendarId } = this.props;
        if (calendarSelectDateTime) {
            // console.log('calendarSelectDateTime', { selectionStartDate, selectionEndDate, selectionCalendarId })
            calendarSelectDateTime({
                start: selectionStartDate,
                end: selectionEndDate,
                calendarId: selectionCalendarId,
            });
            return;
        }
        const { drawerLevel = 1, callback = () => {}, skipAnimal } = this.props;
        const { drawerHeightLarge } = appState;
        const { newSale } = saleStore;
        const { animals } = newSale;
        appState.openDrawer('newCalendarEvent', {
			height: drawerHeightLarge,
            isNew: true,
            hideAdmin: true,
            calendarType: 'booking',
            calendarId: selectionCalendarId,
            animals,
            startDate: selectionStartDate,
            endDate: selectionEndDate,
            skipAnimal,
            callback: async booking => {
                // console.log('newCalendarEvent callback', booking);
                appState.toggleDrawer(false, drawerLevel + 1);
                callback(booking);
                calendarStore.resetDragging();
            },
		}, drawerLevel + 1);
    }

    clickDay = e => {
        // Don't trigger if clicking on an event
        if (e.target.closest('.calendar-event')) {
            return;
        }

        e.stopPropagation();
        e.preventDefault();

        const { enableSelection = true, calendarSelectDateTime, calendarId, calendarDate } = this.props;
        if (!enableSelection) {
            return;
        }

        const { appState, calendarStore, saleStore } = this.props.stores;

        // Set start time to current time of day, rounded to nearest 15 minutes
        const now = new Date();
        const minutes = Math.round(now.getMinutes() / 15) * 15;
        const startDate = new Date(calendarDate);
        startDate.setHours(now.getHours(), minutes, 0, 0);

        const endDate = new Date(startDate);
        endDate.setMinutes(endDate.getMinutes() + 60); // Default 1 hour duration

        // Use the first calendar ID if multiple
        const selectedCalendarId = Array.isArray(calendarId) ? calendarId[0] : calendarId;

        if (calendarSelectDateTime) {
            calendarSelectDateTime({
                start: startDate,
                end: endDate,
                calendarId: selectedCalendarId,
            });
            return;
        }

        const { drawerLevel = 1, callback = () => {}, skipAnimal } = this.props;
        const { drawerHeightLarge } = appState;
        const { newSale } = saleStore;
        const { animals } = newSale;

        appState.openDrawer('newCalendarEvent', {
            height: drawerHeightLarge,
            isNew: true,
            hideAdmin: true,
            calendarType: 'booking',
            calendarId: selectedCalendarId,
            animals,
            startDate,
            endDate,
            skipAnimal,
            callback: async booking => {
                appState.toggleDrawer(false, drawerLevel + 1);
                callback(booking);
                calendarStore.resetDragging();
            },
        }, drawerLevel + 1);
    }

    onEventMouseEnter = (domEvt, ev, currentVisitor, color, bgColor) => {
        const { userStore } = this.props.stores;
        const { customer = {} } = userStore;
        if (parseInt(customer.featureFlags?.hideCalendarPopupPreview, 10) === 1) return;
        clearTimeout(this.hoverHideTimer);
        const { left, top } = clampPos(domEvt.clientX, domEvt.clientY);
        this.hoverShowTimer = setTimeout(() => {
            this.setState({ hoverEvent: { ...ev, currentVisitor, _previewColor: color, _previewBg: bgColor }, previewPos: { left, top } });
        }, 120); // small delay prevents flicker
    };

    onEventMouseMove = (domEvt) => {
        if (!this.state.hoverEvent) return;
        const { left, top } = clampPos(domEvt.clientX, domEvt.clientY);
        this.setState({ previewPos: { left, top } });
    };

    onEventMouseLeave = () => {
        clearTimeout(this.hoverShowTimer);
        this.hoverHideTimer = setTimeout(() => {
            this.setState({ hoverEvent: null });
        }, 180);
    };

    componentWillReceiveProps(nextProps) {
        if ((nextProps.calendarDate !== this.props.calendarDate) || (nextProps.calendarId !== this.props.calendarId)) {
            this.loadAll();
        }
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillUnmount() {
        clearTimeout(this.hoverShowTimer);
        clearTimeout(this.hoverHideTimer);
    }

    render() {
        const { hoverEvent, previewPos } = this.state;
        const  {
            idx,
            calendarId,
            availabilityHoursStart = 0,
            availabilityMinutesStart = 0,
            availabilityHoursEnd = 0,
            availabilityMinutesEnd = 0,
            useCalendarAvailability,
            showPullDownHandle,
            pulldownHandleId,
            color: colorProp,
            backgroundColor: backgroundColorProp,
            calendarDate,
        } = this.props;
        const { calendarStore, calendarEventStore, userStore } = this.props.stores;
        const {
            colors,
            calendarHours,
            startY,
            startYAbs,
            endY,
            selectionStartDate,
            selectionEndDate,
            isToday,
            workhourStart,
            workhourEnd,
        } = calendarStore;

        const { user = {}, isAdmin, isTester, isVeterinary, customer = {} } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { featureFlags = {} } = customer;
        const { hideCalendarPopupPreview, calendarHourHeight, eventCategories } = featureFlags;
        const HEIGHT = parseInt(calendarHourHeight, 10) || DEFAULT_HEIGHT;
        const HEIGHT_SCALE = HEIGHT / 60;

        const workhourStartInt = useCalendarAvailability ? parseInt(availabilityHoursStart, 10) : parseInt(availabilityHoursStart || workhourStart, 10);
        const workhourEndInt = useCalendarAvailability ? parseInt(availabilityHoursEnd || workhourEnd, 10) : parseInt(availabilityHoursEnd || workhourEnd, 10);

        const today = new Date();
        const todayHour = today.getHours();
        const todayMinutes = today.getMinutes();
        const todayHourStart = new Date(today);
        todayHourStart.setHours(todayHour, 0, 0, 0);

        const selectTop = Math.min(startY, endY);
        const selectHeight = endY - startYAbs < 0 ? 0 : Math.abs(endY - startYAbs);
        // const roundedSelectHeight = Math.round(selectHeight / 15) * 15 * HEIGHT_SCALE;
        const roundedSelectHeight = Math.round((selectHeight) / 15) * 15 * HEIGHT_SCALE;

        const selectColor = colors[colors.length - 1];

        const { calendarEvents, recurringList, animals, visitors } = calendarEventStore;

        // Build lookup Maps once per render instead of .find() per event
        const animalMap = new Map();
        if (animals) {
            for (let i = 0; i < animals.length; i++) {
                animalMap.set(animals[i].id, animals[i]);
            }
        }
        const visitorMap = new Map();
        if (visitors) {
            for (let i = 0; i < visitors.length; i++) {
                visitorMap.set(visitors[i].id, visitors[i]);
            }
        }

        const color = colorProp || colors[idx]?.color || '#000000';
        let backgroundColor = backgroundColorProp || colors[idx]?.backgroundColor || '#ffffff';

        return (<>
            <div
                class='no-select'
                onMouseDown={this.handleMouseDown}
                onMouseMove={this.handleMouseMove}
                onMouseUp={this.handleMouseUp}
            >

{/* startY: {startY}<br />
startYAbs: {startYAbs}<br />
endY: {endY}<br />
selectHeight: {selectHeight}<br />
roundedSelectHeight: {roundedSelectHeight}<br /> */}

                {calendarHours.map((todayHour, idx) => {
                    const hourInt = todayHour.getHours();
                    const hour = new Date(calendarDate);
                    hour.setHours(hourInt, 0, 0, 0);
                    const events = calendarEventStore.getEvents(hour, calendarId);
                    const selectionEvent = calendarStore.getSelectionEvent(hour, calendarId);
                    const isInPast = hour < todayHourStart;
                    const isWorkHour = (hourInt >= workhourStartInt) && (hourInt < workhourEndInt);

                    return (<>
                        {events.map((event, eidx) => {
                            const calendarIdx = getCalendarColorIdx(calendarStore.calendars, event.calendarId, 'calendarId');
                            const top = (new Date(event.start).getMinutes() / 60) * 60 * HEIGHT_SCALE;
                            const diff = new Date(event.end) - new Date(event.start);
                            const height = (diff / 1000 / 60 / 60) * 60 * HEIGHT_SCALE;
                            const matchedColor = resolveEventColor(event, eventCategories);
                            let bgColor = matchedColor
                                || getCalendarBackgroundColor(calendarStore.calendars, colors, event.calendarId, 'calendarId')
                                || colors[0]?.backgroundColor || '#ffffff';
                            if (!matchedColor && event.backgroundColor) {
                                bgColor = event.backgroundColor;
                            }
                            let fgColor = colors[calendarIdx]?.color || colors[0]?.color || '#000000';
                            if (event.color) {
                                fgColor = event.color;
                            }
                            const initials = event.calendarId.split('_').map(part => part.charAt(0).toUpperCase()).join('');

                            let currentAnimal = {};
                            let currentVisitor = {};
                            if (event.animals && event.animals.length > 0) {
                                const animalId = event.animals[0].id;
                                const foundAnimal = animalMap.get(animalId);
                                currentAnimal = foundAnimal ? { ...foundAnimal } : {};
                                if (foundAnimal) {
                                    const foundVisitor = visitorMap.get(foundAnimal.owner);
                                    currentVisitor = foundVisitor ? { ...foundVisitor } : {};
                                }
                            }

                            const isCancelled = event.offline >= 1;
                            return (<>
                                <div
                                    class='overflow-hidden ps-1 calendar-event d-flex flex-row rounded'
                                    style={`
                                        background-color: ${isCancelled ? 'var(--bs-secondary-bg)' : (event.hasArrived ? localUtil.shiftColor(bgColor, 0, -70, 20) : bgColor)};
                                        color: ${isCancelled ? 'var(--bs-secondary-color)' : fgColor};
                                        opacity: ${isCancelled ? 0.55 : 0.8};
                                        line-height: 0.8em;
                                        width: ${event.overlapping ? '50%' : '100%'};
                                        border-left: 3px solid ${isCancelled ? 'var(--bs-danger)' : fgColor};
                                        border-top: 1px ${isCancelled ? 'dashed' : 'solid'} ${isCancelled ? 'var(--bs-danger)' : fgColor};
                                        text-decoration: ${isCancelled ? 'line-through' : 'none'};
                                        z-index: 10000;
                                    `}
                                    data-id={event.id}
                                    data-convention-id={event.conventionId}
                                    data-start={event.start}
                                    onClick={this.viewEvent}
                                    onMouseEnter={e => this.onEventMouseEnter(e, event, currentVisitor, color, darkmode ? '#1e1f22' : '#fff')}
                                    onMouseMove={this.onEventMouseMove}
                                    onMouseLeave={this.onEventMouseLeave}
                                >
                                    <small class='font-weight-normal font-style-narrow'>
                                        {isCancelled && <i class='fa-duotone fa-calendar-xmark text-danger me-1' title='Kansellert' />}
                                        {event.hasArrived && <i class='fa-duotone fa-check-circle text-success me-1' />}
                                        {event.skipReminders && <i class='fa-duotone fa-bell-slash text-danger me-1' />}
                                        {event.isRecurring && <i class='fa-duotone fa-repeat me-1' />}
                                        {event.isConvention && <i class='fa-duotone fa-bullseye-pointer me-1' />}

                                        <span class='font-style-narrow'>{util.isoTime(event.start)}: {event.isConvention ? '' : `${initials}: `}{event.title}</span>{event.body && <>: <span class='font-weight-light'>{event.body}</span></>}
                                    </small>
                                </div>
                            </>);
                            lastOneIsOverlapping = event.overlapping;
                        })}
                    </>);
                })}
            </div>
            {hoverEvent && parseInt(hideCalendarPopupPreview, 10) !== 1 && (
                <EventPreview
                    ev={hoverEvent}
                    currentVisitor={hoverEvent.currentVisitor}
                    pos={previewPos}
                    color={hoverEvent._previewColor}
                    bgColor={hoverEvent._previewBg}
                />
            )}
        </>);
    }
}

export default CalendarHourGrid;
